import { supabase } from './supabase';
import { cache as fabricCache } from 'fabric';

export interface CustomFont {
  id: string;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  displayName: string;
  fileName: string;
  fileUrl: string;
  fileFormat: string;
  fileSize: number;
  isActive: boolean;
  postscriptName: string;
  serverAvailable: boolean;
  storagePath: string | null;
}

export interface ServerFontEntry {
  fileName: string;
  postscriptName: string;
}

let serverFontManifest: ServerFontEntry[] = [];

export interface FontFamily {
  family: string;
  displayName: string;
  category: 'custom';
  variants: FontVariant[];
  isLoaded: boolean;
}

export interface FontVariant {
  weight: string;
  style: string;
  displayName: string;
  id?: string;
}

const loadedFonts = new Set<string>();

// Fabric v6 caches character advance widths per (family, style_weight) in a
// module-level cache. Any FabricText/Textbox measured BEFORE its font variant
// finished loading poisons that cache with fallback-font metrics, and
// initDimensions() re-reads the poisoned entries forever. Purge the family's
// cache exactly once when a variant transitions from not-ready to loaded so
// the next initDimensions() re-measures with real glyph metrics.
const purgedVariantCaches = new Set<string>();

function purgeFabricFontCacheOnce(
  fontFamily: string,
  weight: string,
  style: string
): void {
  const key = `${fontFamily}|${weight}|${style}`;
  if (purgedVariantCaches.has(key)) return;
  purgedVariantCaches.add(key);
  try {
    fabricCache.clearFontCache(fontFamily);
  } catch (err) {
    console.warn(`[FontLoader] Failed to clear Fabric font cache for "${fontFamily}":`, err);
  }
}

let customFonts: CustomFont[] = [];
let isCustomInitialized = false;
let customInitPromise: Promise<void> | null = null;

export function cssFormat(format: string): string {
  switch (format) {
    case 'woff2': return 'woff2';
    case 'woff': return 'woff';
    case 'ttf': return 'truetype';
    case 'otf': return 'opentype';
    default: return 'woff2';
  }
}

function injectCustomFontFace(font: CustomFont): void {
  const styleId = `cs-font-${font.id}`;
  if (document.getElementById(styleId)) return;

  const format = cssFormat(font.fileFormat);
  const style = document.createElement('style');
  style.id = styleId;

  const groupedFace = `
    @font-face {
      font-family: '${font.fontFamily}';
      src: url('${font.fileUrl}') format('${format}');
      font-weight: ${font.fontWeight};
      font-style: ${font.fontStyle};
      font-display: swap;
    }
  `;

  const postscript = font.postscriptName && font.postscriptName !== font.fontFamily
    ? `
    @font-face {
      font-family: '${font.postscriptName}';
      src: url('${font.fileUrl}') format('${format}');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `
    : '';

  style.textContent = groupedFace + postscript;
  document.head.appendChild(style);
}

function removeCustomFontFace(fontId: string): void {
  const el = document.getElementById(`cs-font-${fontId}`);
  if (el) el.remove();
}

export async function loadCustomFont(fontFamily: string): Promise<boolean> {
  if (loadedFonts.has(fontFamily)) return true;

  const exists = customFonts.some(f => f.fontFamily === fontFamily);
  if (!exists) return false;

  try {
    const variants = customFonts.filter(f => f.fontFamily === fontFamily);
    const loadPromises = variants.map(v => {
      const style = v.fontStyle === 'italic' ? 'italic' : 'normal';
      const weight = String(v.fontWeight || 400);
      return document.fonts.load(`${style} ${weight} 16px "${fontFamily}"`);
    });
    await Promise.all(loadPromises);
    loadedFonts.add(fontFamily);
    return true;
  } catch (err) {
    // Do NOT mark the family loaded on failure — doing so poisons every
    // downstream readiness check (isFontLoaded, sync gating) and lets
    // fallback-metric measurements masquerade as correct.
    console.error(`[FontLoader] Failed to load font "${fontFamily}":`, err);
    return false;
  }
}

export function isFontLoaded(family: string): boolean {
  if (customFonts.some(f => f.fontFamily === family)) return true;
  return loadedFonts.has(family);
}

export function isFontReady(fontFamily: string): boolean {
  try {
    return document.fonts.check(`16px "${fontFamily}"`);
  } catch {
    return false;
  }
}

/**
 * Variant-aware readiness check. `isFontReady(family)` returns true the moment
 * ANY variant in the family is loaded, which lets stale-face measurement sneak
 * through after a variant swap and produces the extra-right-space / clipped-
 * glyph regression. This checks the specific (family, weight, style) triple so
 * the canvas only measures against the actually-painted face.
 */
export function isVariantReady(
  fontFamily: string,
  fontWeight: number | string | undefined,
  fontStyle: string | undefined
): boolean {
  try {
    const weight = normalizeWeight(fontWeight);
    const style = normalizeStyle(fontStyle);
    return document.fonts.check(`${style} ${weight} 16px "${fontFamily}"`);
  } catch {
    return false;
  }
}

export async function ensureVariantLoaded(
  fontFamily: string,
  fontWeight: number | string | undefined,
  fontStyle: string | undefined
): Promise<void> {
  try {
    const weight = normalizeWeight(fontWeight);
    const style = normalizeStyle(fontStyle);
    const wasReady = isVariantReady(fontFamily, weight, style);
    await document.fonts.load(`${style} ${weight} 16px "${fontFamily}"`);
    loadedFonts.add(fontFamily);
    if (!wasReady && isVariantReady(fontFamily, weight, style)) {
      // Face just transitioned to loaded — purge any fallback-metric
      // measurements Fabric cached before this point.
      purgeFabricFontCacheOnce(fontFamily, weight, style);
    }
  } catch (err) {
    // Loudly report — a silent failure here means measurement runs against
    // fallback metrics with no diagnostic trail (the Bold misalignment class).
    console.error(
      `[FontLoader] ensureVariantLoaded failed for "${fontFamily}" ${fontWeight} ${fontStyle}:`,
      err
    );
  }
}

export async function initCustomFonts(): Promise<void> {
  if (customInitPromise) return customInitPromise;
  if (isCustomInitialized) return;

  customInitPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('cs_fonts')
        .select('*')
        .eq('is_active', true)
        .order('font_family')
        .order('font_weight');

      if (error) {
        console.warn('[FontLoader] Failed to fetch custom fonts:', error.message);
        customFonts = [];
      } else {
        customFonts = (data || []).map((row: any) => ({
          id: row.id,
          fontFamily: row.font_family,
          fontWeight: row.font_weight,
          fontStyle: row.font_style,
          displayName: row.font_display_name,
          fileName: row.file_name,
          fileUrl: row.file_url,
          fileFormat: row.file_format,
          fileSize: row.file_size,
          isActive: row.is_active,
          postscriptName: row.postscript_name || row.file_name.replace(/\.(woff2|woff|ttf|otf)$/i, ''),
          serverAvailable: row.server_available !== false,
          storagePath: row.storage_path || null,
        }));

        for (const font of customFonts) {
          injectCustomFontFace(font);
        }

        console.log(
          `[FontLoader] Loaded ${customFonts.length} custom font variants across ${new Set(customFonts.map(f => f.fontFamily)).size} families`
        );
      }

      const { data: manifestRows, error: manifestErr } = await supabase
        .from('cs_server_fonts')
        .select('file_name, postscript_name')
        .order('postscript_name');

      if (manifestErr) {
        console.warn('[FontLoader] Failed to fetch server manifest:', manifestErr.message);
        serverFontManifest = [];
      } else {
        serverFontManifest = (manifestRows || []).map((r: any) => ({
          fileName: r.file_name,
          postscriptName: r.postscript_name,
        }));
      }

      isCustomInitialized = true;
    } catch (err) {
      console.warn('[FontLoader] Error initializing custom fonts:', err);
    } finally {
      customInitPromise = null;
    }
  })();

  return customInitPromise;
}

export function getCustomFonts(): CustomFont[] {
  return customFonts;
}

export function getFontsByFamily(family: string): CustomFont[] {
  return customFonts
    .filter((f) => f.fontFamily === family && f.isActive)
    .slice()
    .sort((a, b) => {
      const wa = parseInt(a.fontWeight, 10) || 400;
      const wb = parseInt(b.fontWeight, 10) || 400;
      if (wa !== wb) return wa - wb;
      // normal before italic at same weight
      const sa = normalizeStyle(a.fontStyle);
      const sb = normalizeStyle(b.fontStyle);
      if (sa === sb) return 0;
      return sa === 'normal' ? -1 : 1;
    });
}

export function getFontFamilies(): string[] {
  const set = new Set<string>();
  for (const f of customFonts) {
    if (f.isActive) set.add(f.fontFamily);
  }
  return Array.from(set).sort();
}

export function getFontVariantById(id: string | undefined | null): CustomFont | null {
  if (!id) return null;
  return customFonts.find((f) => f.id === id) || null;
}

/**
 * Find the `cs_fonts` row that best matches an element's (family, weight, style) triple.
 * Used by the legacy-repair pass in `setActiveProject` to attach `fontVariantId`
 * to elements saved before variants were first-class.
 */
export function findVariantForTriple(
  family: string,
  weight: number | string | undefined,
  style: string | undefined
): CustomFont | null {
  const siblings = getFontsByFamily(family);
  if (siblings.length === 0) return null;
  const targetWeight = parseInt(normalizeWeight(weight), 10);
  const targetStyle = normalizeStyle(style);

  const exact = siblings.find(
    (f) =>
      parseInt(normalizeWeight(f.fontWeight), 10) === targetWeight &&
      normalizeStyle(f.fontStyle) === targetStyle
  );
  if (exact) return exact;

  // closest weight within same style
  const sameStyle = siblings.filter((f) => normalizeStyle(f.fontStyle) === targetStyle);
  const pool = sameStyle.length > 0 ? sameStyle : siblings;
  pool.sort((a, b) => {
    const da = Math.abs(parseInt(normalizeWeight(a.fontWeight), 10) - targetWeight);
    const db = Math.abs(parseInt(normalizeWeight(b.fontWeight), 10) - targetWeight);
    return da - db;
  });
  return pool[0] || null;
}

/**
 * Convenience: given a variant, ensure its @font-face is registered and the
 * browser has finished downloading/parsing the actual file. Required before
 * Fabric measures the textbox so advance widths are accurate.
 */
export async function loadVariant(variant: CustomFont): Promise<void> {
  try {
    const style = normalizeStyle(variant.fontStyle);
    const weight = normalizeWeight(variant.fontWeight);
    const wasReady = isVariantReady(variant.fontFamily, weight, style);
    await document.fonts.load(`${style} ${weight} 16px "${variant.fontFamily}"`);
    loadedFonts.add(variant.fontFamily);
    if (!wasReady && isVariantReady(variant.fontFamily, weight, style)) {
      purgeFabricFontCacheOnce(variant.fontFamily, weight, style);
    }
  } catch (err) {
    console.warn(`[FontLoader] Variant load failed for ${variant.postscriptName}:`, err);
  }
}

export function getServerFontManifest(): ServerFontEntry[] {
  return serverFontManifest;
}

export function isPostscriptOnServer(postscriptName: string): boolean {
  if (!postscriptName) return false;
  return serverFontManifest.some((e) => e.postscriptName === postscriptName);
}

function normalizeWeight(w: number | string | undefined): string {
  if (w === undefined || w === null || w === '') return '400';
  const n = typeof w === 'number' ? w : parseInt(String(w), 10);
  if (Number.isFinite(n) && n > 0) return String(n);
  return '400';
}

function normalizeStyle(s: string | undefined): string {
  if (!s) return 'normal';
  const lower = String(s).toLowerCase();
  if (lower === 'italic' || lower === 'oblique') return 'italic';
  return 'normal';
}

export interface ResolvedPostscript {
  postscriptName: string;
  matched: boolean;
  exactMatch: boolean;
  serverAvailable: boolean;
  font?: CustomFont;
}

export function resolvePostscriptName(
  fontFamily: string,
  fontWeight: number | string | undefined,
  fontStyle: string | undefined
): ResolvedPostscript {
  const targetWeight = normalizeWeight(fontWeight);
  const targetStyle = normalizeStyle(fontStyle);

  // If the caller hands us a PostScript name directly (the SVG-import path
  // does this routinely), honor it verbatim — the PostScript already encodes
  // weight and style, and the requested weight/style args may be stale
  // defaults from an earlier render. Mixing the two identity spaces was the
  // cause of "wrong variant after reopening an SVG" reports.
  const psMatch = customFonts.find((f) => f.postscriptName === fontFamily);
  if (psMatch) {
    return {
      postscriptName: psMatch.postscriptName,
      matched: true,
      exactMatch: true,
      serverAvailable: psMatch.serverAvailable && isPostscriptOnServer(psMatch.postscriptName),
      font: psMatch,
    };
  }

  const familyMatches = customFonts.filter((f) => f.fontFamily === fontFamily);

  if (familyMatches.length === 0) {
    return { postscriptName: fontFamily, matched: false, exactMatch: false, serverAvailable: false };
  }

  const exact = familyMatches.find(
    (f) => normalizeWeight(f.fontWeight) === targetWeight && normalizeStyle(f.fontStyle) === targetStyle
  );

  if (exact) {
    return {
      postscriptName: exact.postscriptName,
      matched: true,
      exactMatch: true,
      serverAvailable: exact.serverAvailable && isPostscriptOnServer(exact.postscriptName),
      font: exact,
    };
  }

  const styleMatch = familyMatches.find((f) => normalizeStyle(f.fontStyle) === targetStyle) || familyMatches[0];
  return {
    postscriptName: styleMatch.postscriptName,
    matched: true,
    exactMatch: false,
    serverAvailable: styleMatch.serverAvailable && isPostscriptOnServer(styleMatch.postscriptName),
    font: styleMatch,
  };
}

export async function reloadCustomFonts(): Promise<void> {
  loadedFonts.clear();
  for (const font of customFonts) {
    removeCustomFontFace(font.id);
  }
  isCustomInitialized = false;
  customInitPromise = null;
  await initCustomFonts();
}

const WEIGHT_PATTERNS: [RegExp, string, string][] = [
  [/hairline/i, '100', 'Hairline'],
  [/thin/i, '100', 'Thin'],
  [/ultra[\s_-]?light/i, '200', 'Ultra Light'],
  [/extra[\s_-]?light/i, '200', 'Extra Light'],
  [/light/i, '300', 'Light'],
  [/\blt\b/i, '300', 'Light'],
  [/book/i, '400', 'Book'],
  [/regular/i, '400', 'Regular'],
  [/\bnormal\b/i, '400', 'Regular'],
  [/medium/i, '500', 'Medium'],
  [/\bmd\b/i, '500', 'Medium'],
  [/semi[\s_-]?bold/i, '600', 'Semi Bold'],
  [/demi[\s_-]?bold/i, '600', 'Demi Bold'],
  [/extra[\s_-]?bold/i, '800', 'Extra Bold'],
  [/ultra[\s_-]?bold/i, '800', 'Ultra Bold'],
  [/bold/i, '700', 'Bold'],
  [/\bbd\b/i, '700', 'Bold'],
  [/heavy/i, '900', 'Heavy'],
  [/black/i, '900', 'Black'],
  [/\bblk\b/i, '900', 'Black'],
];

export interface DetectedFontMeta {
  weight: string;
  weightLabel: string;
  style: string;
  displayName: string;
}

export function detectFontMeta(fileName: string): DetectedFontMeta {
  const nameWithoutExt = fileName.replace(/\.(woff2?|ttf|otf)$/i, '');
  const normalized = nameWithoutExt.replace(/[-_]/g, ' ');

  let style = 'normal';
  let remaining = normalized;
  if (/italic/i.test(remaining)) {
    style = 'italic';
    remaining = remaining.replace(/italic/gi, '');
  } else if (/oblique/i.test(remaining)) {
    style = 'italic';
    remaining = remaining.replace(/oblique/gi, '');
  }

  let weight = '400';
  let weightLabel = 'Regular';
  for (const [pattern, w, label] of WEIGHT_PATTERNS) {
    if (pattern.test(remaining)) {
      weight = w;
      weightLabel = label;
      break;
    }
  }

  let displayName: string;
  if (style === 'italic') {
    displayName = weightLabel === 'Regular' ? 'Italic' : `${weightLabel} Italic`;
  } else {
    displayName = weightLabel;
  }

  return { weight, weightLabel, style, displayName };
}

export function getFileFormat(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['woff2', 'woff', 'ttf', 'otf'].includes(ext)) return ext;
  return 'otf';
}
